import React, { memo } from 'react';

import {
  Alert,
  Breakpoints,
  CTA,
  ExternalLinkIndicator,
  styled,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';

import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import IncentiveConstants from '../../hooks-store/incentive-store/actions/incentive-constants';
import { useStore } from '../../hooks-store/store';

const ContentCardWrapper: any = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  gap: 24px;
`;

interface ImageContainerProps {
  images: any;
  faHostServerUrl: string;
}

const ImageContainer: any = styled.div<ImageContainerProps>`
  ${({ images, faHostServerUrl }) => {
    return `
      align-self: stretch;
      background: url(${faHostServerUrl + images?.mobile || ''}) lightgray 50% / cover no-repeat;
      height: 0;
      padding-bottom: 100%;
      width: 100%;
      
      @media (min-width: ${Breakpoints.b560}px) {
        background: url(${faHostServerUrl + images?.tablet || ''}) lightgray 50% / cover no-repeat;
        padding-bottom: calc(200/197.33 * 100%);
      }

      @media (min-width: ${Breakpoints.b1600}px) {
        background: url(${faHostServerUrl + images?.desktop || ''}) lightgray 50% / cover no-repeat;
        padding-bottom: calc(328/485.33 * 100%);
      }
    `;
  }}
`;

const InformationContainer: any = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.size.static150};
`;

const ErrorMessageContainer: any = styled.div`
  ${({ theme }) => {
    return `
      display: flex;
      gap: ${theme.size.static150};
      height: 100%;
      align-items: flex-start;
    `;
  }}
`;

const AlertIconWrapper = styled.div`
  color: red;
  fill: red;
  display: flex;
  align-items: center;
`;

const MessageContainer: any = styled.div`
  ${({ theme }) => {
    return `
    padding-top: ${theme.size.static150};
  `;
  }}
`;

interface ContentCardProps {
  images?: any;
  redirectName: string;
  title: string;
  url: string;
  isDisabled?: boolean;
  setLiteratureData: (value: any) => void;
}

export const ContentCard = memo(
  ({
    images,
    redirectName,
    title,
    url,
    isDisabled,
    setLiteratureData,
  }: ContentCardProps) => {
    const { translationHandler } = useMaintenanceContext();
    const { faHostServerUrl } = useFeatureAppConfig();
    const [
      store,
      { [IncentiveConstants.UpdateStoreByFormInput]: updateStoreByFormInput },
    ] = useStore();
    const { form } = store;

    const handleRedirect = async (url: string) => {
      window.open(url, '_blank');
    };

    return (
      <ContentCardWrapper>
        <ImageContainer images={images} faHostServerUrl={faHostServerUrl} />
        <InformationContainer>
          <ErrorMessageContainer>
            {isDisabled && (
              <AlertIconWrapper>
                <Alert />
              </AlertIconWrapper>
            )}
            <TextCustom
              appearance={TextAppearance.headline200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight20,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize24,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight28,
                },
              }}
            >
              {title}
            </TextCustom>
          </ErrorMessageContainer>
          {isDisabled ? (
            <MessageContainer>
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight20,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize20,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {translationHandler(
                  'literature.downloadables.relatedContent.document.not.available',
                )}{' '}
                <CTA
                  emphasis="tertiary"
                  tag="button"
                  onClick={() => {
                      updateStoreByFormInput({ ...form, selectedTab: 'vin' });
                      setLiteratureData([]);
                    }
                  }
                >
                  {translationHandler(
                    'literature.downloadables.relatedContent.document.not.available.redirectLabel',
                  )}
                </CTA>
              </TextCustom>
            </MessageContainer>
          ) : (
            <CTA
              aria-label={`Go to ${title}`}
              emphasis="tertiary"
              onClick={isDisabled ? undefined : () => handleRedirect(url)}
              stretchContent
              tag="a"
              target="_blank"
            >
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight20,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize20,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {redirectName} <ExternalLinkIndicator />
              </TextCustom>
            </CTA>
          )}
        </InformationContainer>
      </ContentCardWrapper>
    );
  },
);
